import { type DeferredPromise } from "DeferredPromise";
import { type TaskShellOptions, type UIExtensionSettings } from "FormFlowTypes";
import {
  type ExtendedFormFlowUI,
  type FormFlowUI,
  type FormFlowUIMenuOptions,
  type FormOptions,
  type ShellLoaderOptions,
} from "FormFlowUITypes";
import global from "Global";
import { trackBusyStateAsync } from "GlobalBusyStateTracker";
import { waitForValueAsync } from "KnockoutExtensions";
import RunAsClientMessageBarProvider from "RunAsClientMessageBarProvider";
import TaskViewModel from "TaskViewModel";
import CancelShortcutExtension from "UIExtensions/CancelShortcut";
import InitialFocusExtension from "UIExtensions/InitialFocus";
import ScanInputExtension from "UIExtensions/ScanInput";
import ValidationRegistrar from "ValidationRegistrar";
import type { Entity } from "breeze-client";
import "gwResizable";
import { type Observable } from "knockout";
import { buildAdditionalMenuOptions, buildFormFlowUIMenuAsync } from "./FormFlowUIMenuProvider.ts";
import {
  cancelTaskAsync,
  handleFooterTransitionAsync,
  handleScanTransitionAsync,
  setupMessageBusSubscriptions,
} from "./FormFlowUIMessageBus.ts";
import { buildFormFlowUI } from "./FormFlowUIProvider.ts";
import { ensureSavedAsync, handleSaveChangesAsync } from "./FormFlowUISaveChanges.ts";

export async function loadShellAsync(
  formOptions: FormOptions,
  shellLoaderOptions: ShellLoaderOptions,
): Promise<DeferredPromise<void>> {
  const formFlowUI = buildFormFlowUI(formOptions, shellLoaderOptions);
  await loadShellForFormFlowUIAsync(formFlowUI);

  return formFlowUI.formAwaiter;
}

export async function loadShellForFormFlowUIAsync(formFlowUI: ExtendedFormFlowUI): Promise<void> {
  const menuOptions = await buildFormFlowUIMenuAsync(formFlowUI);
  const viewModel = createViewModel(formFlowUI, menuOptions, () => formFlowUI.isAborted);
  const entity = formFlowUI.entity?.() !== undefined ? formFlowUI.entity : {};

  setupMessageBusSubscriptions(formFlowUI);

  const shellOptions: TaskShellOptions = {
    additionalMenuOptions: buildAdditionalMenuOptions(formFlowUI, menuOptions),
    pageExtensions: {
      dataItem: entity,
      messageBus: formFlowUI.messageBus,
      sessionData: {
        configurationParentID: formFlowUI.configurationParentID,
        clientPK: formFlowUI.clientPK,
      },
      validationRegistrar: new ValidationRegistrar(),
      workflowContext: formFlowUI.workflowContext,
      modelProvider: formFlowUI.modelProvider,
    },
    viewModel,
    configurationParentID: formFlowUI.configurationParentID,
    uiContextOptions: formFlowUI.uiContextOptions,
  };

  trackBusyStateAsync(
    (async (): Promise<void> => {
      const [$shell, shellBindingContext] = await formFlowUI.shellProviderFn(formFlowUI.formId, shellOptions);

      if ($.contains(document.body, $shell[0])) {
        formFlowUI.$shell = $shell;
        formFlowUI.formBindingContext = shellBindingContext;

        await applyUIExtensionsAsync(formFlowUI);
      }
    })(),
  );
}

function createViewModel(
  formFlowUI: FormFlowUI,
  menuOptions: FormFlowUIMenuOptions,
  isAbortedFn?: () => boolean,
): TaskViewModel {
  const messageBarProvider = formFlowUI.clientPK ? new RunAsClientMessageBarProvider(formFlowUI.clientPK) : undefined;
  return new TaskViewModel(
    formFlowUI.entity as Observable<Entity | undefined>,
    formFlowUI.notificationSummary,
    formFlowUI.buttonConfig?.footerTransitions,
    cancelTaskAsync.bind(undefined, formFlowUI),
    handleSaveChangesAsync.bind(undefined, formFlowUI),
    ensureSavedAsync.bind(undefined, formFlowUI),
    handleFooterTransitionAsync.bind(undefined, formFlowUI),
    typeof formFlowUI.title === "string" ? { captionOverride: formFlowUI.title } : formFlowUI.title,
    formFlowUI.buttonConfig?.hideFooter,
    formFlowUI.buttonConfig?.hideSaveButtons,
    formFlowUI.buttonConfig?.hideCloseButtons,
    formFlowUI.buttonConfig?.hideBackButtons,
    messageBarProvider,
    formFlowUI.buttonConfig?.disableSaveCancel ? (): void => formFlowUI.formAwaiter.resolve() : undefined,
    menuOptions.canShowEDocs,
    menuOptions.showEDocsAsync,
    menuOptions.canShowLogs,
    menuOptions.showLogsAsync,
    menuOptions.canShowMessages,
    menuOptions.showMessagesAsync,
    menuOptions.canShowNotes,
    menuOptions.showNotesAsync,
    menuOptions.canShowWorkflow,
    menuOptions.canShowDocuments,
    menuOptions.getDocumentMenuItemsAsync,
    formFlowUI.hasChanges,
    formFlowUI.entityUrl,
    formFlowUI.isFavorite,
    formFlowUI.toggleFavoriteAsync,
    isAbortedFn,
  );
}

async function applyUIExtensionsAsync(formFlowUI: FormFlowUI): Promise<void> {
  const $shell = formFlowUI.$shell;
  const extensionSettings: UIExtensionSettings[] = [
    [new ScanInputExtension($shell, handleScanTransitionAsync.bind(undefined, formFlowUI)), false],
  ];

  if (!global.materialDesign) {
    extensionSettings.push([new CancelShortcutExtension($shell, cancelTaskAsync.bind(undefined, formFlowUI)), false]);
    extensionSettings.push([new InitialFocusExtension($shell), true]);
  }

  for (const [extension, runWhenIdle] of extensionSettings) {
    if (runWhenIdle) {
      waitForBusyStateAndApplyExtensionAsync(formFlowUI.isBusy, extension);
    } else {
      await extension.applyAsync();
    }
  }

  async function waitForBusyStateAndApplyExtensionAsync(
    isBusy: Observable<boolean>,
    extension: CancelShortcutExtension | ScanInputExtension | InitialFocusExtension,
  ): Promise<void> {
    await waitForValueAsync(isBusy, false);
    await extension.applyAsync();
  }
}
